import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics tracking function
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
  }
};

// Track page views
export const trackPageView = (page_path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-64KE66VJXF', {
      page_path: page_path
    });
  }
};

// Analytics component to track page views
export const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null; // This component doesn't render anything
};

// Custom hooks for tracking specific events
export const useAnalytics = () => {
  // Track when a tool is opened/viewed
  const trackToolUsage = useCallback((toolName) => {
    trackEvent('tool_viewed', 'engagement', toolName);
    console.log(`[Analytics] Tool viewed: ${toolName}`);
  }, []);

  // Track when text analysis is performed
  const trackTextAnalysis = useCallback((toolName, textLength, success = true) => {
    const eventName = success ? 'text_analyzed' : 'analysis_failed';
    trackEvent(eventName, 'engagement', toolName, textLength);
    console.log(`[Analytics] ${eventName}: ${toolName}, length: ${textLength}`);
  }, []);

  // Track copy actions
  const trackCopyAction = useCallback((toolName) => {
    trackEvent('copy_action', 'engagement', toolName);
    console.log(`[Analytics] Copy action: ${toolName}`);
  }, []);

  // Track download actions
  const trackDownloadAction = useCallback((toolName) => {
    trackEvent('download_action', 'engagement', toolName);
    console.log(`[Analytics] Download action: ${toolName}`);
  }, []);

  // Track feedback submissions
  const trackFeedback = useCallback((toolName, feedbackType) => {
    trackEvent('feedback_submitted', 'engagement', `${toolName}_${feedbackType}`);
    console.log(`[Analytics] Feedback: ${toolName} - ${feedbackType}`);
  }, []);

  // Track newsletter subscriptions
  const trackSubscription = useCallback((email) => {
    trackEvent('newsletter_signup', 'conversion', 'email_signup');
    console.log(`[Analytics] Newsletter signup`);
  }, []);

  // Track errors
  const trackError = useCallback((toolName, errorType, errorDetails = '') => {
    trackEvent('error_occurred', 'errors', `${toolName}_${errorType}`);
    console.log(`[Analytics] Error: ${toolName} - ${errorType}`, errorDetails);
  }, []);

  // Track feature usage (tabs, modes, filters, etc.)
  const trackFeatureUsage = useCallback((toolName, featureName, featureValue = '') => {
    trackEvent('feature_used', 'engagement', `${toolName}_${featureName}`, featureValue);
    console.log(`[Analytics] Feature used: ${toolName} - ${featureName} - ${featureValue}`);
  }, []);

  // Track quick example usage
  const trackQuickExample = useCallback((toolName, exampleText = '') => {
    trackEvent('quick_example_used', 'engagement', toolName);
    console.log(`[Analytics] Quick example used: ${toolName}`);
  }, []);

  // Track API performance
  const trackAPIPerformance = useCallback((toolName, endpoint, duration, success = true) => {
    trackEvent('api_call', 'performance', `${toolName}_${endpoint}`, duration);
    console.log(`[Analytics] API call: ${toolName} - ${endpoint} - ${duration}ms - ${success ? 'success' : 'failed'}`);
  }, []);

  // Track time spent on a tool
  const trackTimeSpent = useCallback((toolName, durationSeconds) => {
    trackEvent('time_spent', 'engagement', toolName, durationSeconds);
    console.log(`[Analytics] Time spent: ${toolName} - ${durationSeconds}s`);
  }, []);

  // Track tool mode changes (e.g., single vs batch mode)
  const trackModeChange = useCallback((toolName, mode) => {
    trackEvent('mode_changed', 'engagement', `${toolName}_${mode}`);
    console.log(`[Analytics] Mode changed: ${toolName} - ${mode}`);
  }, []);

  // Track affiliate link clicks
  const trackAffiliateClick = useCallback((productName, context) => {
    trackEvent('affiliate_click', 'conversion', `${productName}_${context}`);
    console.log(`[Analytics] Affiliate click: ${productName} - ${context}`);
  }, []);

  return {
    trackToolUsage,
    trackTextAnalysis,
    trackCopyAction,
    trackDownloadAction,
    trackFeedback,
    trackSubscription,
    trackError,
    trackFeatureUsage,
    trackQuickExample,
    trackAPIPerformance,
    trackTimeSpent,
    trackModeChange,
    trackAffiliateClick
  };
}; 